import React, { useCallback, useRef, useState } from "react"
import { List, ListItem, Stack, StackProps } from "@mui/material"
import { FaIcon } from "../../../../mp-core/icons"
import IconButton from "../IconButton"
import { FACE_NAMES, FACE_ARIA_LABELS } from "./constants"
import { getTabIndex, getNextIndex } from "./helpers"
import { useStyles } from "./styles"
import { ExperienceRating } from "./types"

interface Props extends Omit<StackProps, "value" | "onChange"> {
  selectedRate: ExperienceRating
  onChange: (value: ExperienceRating) => void
  disabled?: boolean
  ["data-automation-id"]?: string
  ["aria-labelledby"]?: string
  ["aria-label"]?: string
}

const ExperienceRatingScale: React.FC<Props> = ({
  selectedRate,
  onChange,
  disabled = false,
  "data-automation-id": dataAutomationId,
  "aria-labelledby": ariaLabelledBy,
  "aria-label": ariaLabel,
  className,
  ...rest
}) => {
  const { classes, cx } = useStyles({ disabled })
  const radioRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (disabled) return

      const nextIndex = getNextIndex(e.key, index)

      if (nextIndex !== null) {
        e.preventDefault()
        setFocusedIndex(nextIndex)
        radioRefs.current[nextIndex]?.focus()
      }
    },
    [disabled]
  )

  const handleFocus = useCallback((index: number) => {
    setFocusedIndex(index)
  }, [])

  const handleBlur = useCallback(() => {
    setFocusedIndex(-1)
  }, [])

  const handleClick = useCallback(
    (faceType: ExperienceRating) => {
      if (disabled) return
      onChange(selectedRate === faceType ? null : faceType)
    },
    [disabled, onChange, selectedRate]
  )

  return (
    <Stack
      className={cx(classes.root, className)}
      data-automation-id={dataAutomationId}
      {...rest}
    >
      <List
        role="radiogroup"
        aria-labelledby={ariaLabelledBy}
        aria-label={ariaLabel}
        disablePadding
        sx={{ display: "flex" }}
      >
        {FACE_NAMES.map((faceType, index) => (
          <ListItem
            key={faceType}
            role="none"
            sx={{ width: "auto", padding: 1 }}
          >
            <IconButton
              ref={(el: HTMLButtonElement | null) => {
                radioRefs.current[index] = el
              }}
              role="radio"
              className={cx(classes.button, {
                [classes.selectedButton]: selectedRate === faceType
              })}
              disabled={disabled}
              aria-label={FACE_ARIA_LABELS[faceType]}
              aria-checked={selectedRate === faceType}
              tabIndex={getTabIndex(
                index,
                faceType,
                focusedIndex,
                selectedRate,
                disabled
              )}
              onClick={() => handleClick(faceType)}
              onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, index)}
              onFocus={() => handleFocus(index)}
              onBlur={handleBlur}
              data-automation-id={
                dataAutomationId ? `${dataAutomationId}-${faceType}` : undefined
              }
            >
              <FaIcon
                solid
                size="unset"
                name={`face-${faceType}`}
                className={cx(classes.faceIcon, {
                  [classes[faceType]]: selectedRate === faceType,
                  [classes.notSelectedFaceIcon]:
                    !!selectedRate && selectedRate !== faceType
                })}
              />
            </IconButton>
          </ListItem>
        ))}
      </List>
    </Stack>
  )
}

export default ExperienceRatingScale
